/**
 * X (Twitter) Ads — every call to /slighter_crm/x-ads, typed after the JSON the backend
 * returns (backend/src/modules/slighter-crm/x-ads). Optional fields are omitted by the
 * backend when X sends nothing, so they are optional here too.
 *
 * Errors come back as `{ ok:false, code, message }`; the shared axios client toasts them.
 * `X_RECONNECT_REQUIRED` (X refused the saved tokens) is announced to `onReconnectRequired`
 * listeners so every screen can show "Reconnect X" instead of a dead end.
 */
import { api } from '@/config/api';

// ── Connection ────────────────────────────────────────────────────────────────
export interface XConfig { configured: boolean; sandboxDefault: boolean; callbackUrl: string }

export interface XConstants {
  objectives: string[];
  productTypes: string[];
  placements: string[];
  bidStrategies: string[];
  goals: string[];
  payBy: string[];
  ageBuckets: string[];
  genders: Array<{ value: string; label: string }>;
}

/** One connected X user and the ads account picked for it. */
export interface XAccount {
  _id: string;
  accountUid: string;
  companyUid: string;
  xUserId: string;
  screenName?: string;
  sandbox: boolean;
  adsAccountId?: string;
  adsAccountName?: string;
  timezone?: string;
  currency?: string;
  fundingInstrumentId?: string;
  promotableUserId?: string;
  promotableScreenName?: string;
  permissions?: string[];
  isDefault: boolean;
  isActive: boolean;
  lastSyncedAt?: string;
  lastSyncError?: string;
  healthCheckedAt?: string;
  healthError?: string;
  connectedByUserUid: string;
  createdAt?: string;
  updatedAt?: string;
  /** Only on the select / sandbox-account replies. */
  warnings?: string[];
}

export type XHealthStatus = 'connected' | 'needs_account' | 'needs_setup' | 'expired' | 'unavailable';
export interface XHealth { status: XHealthStatus; message: string; checkedAt: string }

export interface XAdsAccountOption { id: string; name?: string; businessName?: string; timezone?: string; approvalStatus?: string; createdAt?: string }

export interface XFundingInstrument {
  id: string;
  description?: string;
  type?: string;
  currency?: string;
  entityStatus?: string;
  ableToFund: boolean;
  reasonsNotAbleToFund?: unknown[];
  startTime?: string;
  endTime?: string;
  creditLimitLocalMicro?: number;
  fundedAmountLocalMicro?: number;
}

export type XLookupKind = 'locations' | 'languages' | 'interests' | 'platforms';
export interface XTargetingOption { targetingValue?: string; name?: string; targetingType?: string; countryCode?: string; locationType?: string }

// ── Campaigns ─────────────────────────────────────────────────────────────────
export interface XCampaign {
  id: string;
  name?: string;
  entityStatus?: string;
  effectiveStatus?: string;
  servable?: boolean;
  reasonsNotServable?: string[];
  dailyBudgetLocalMicro?: number;
  totalBudgetLocalMicro?: number;
  currency?: string;
  fundingInstrumentId?: string;
  createdAt?: string;
  updatedAt?: string;
  deleted: boolean;
}

export interface XCampaignInput { name: string; dailyBudgetMicros: number; totalBudgetMicros?: number; fundingInstrumentId?: string; status?: 'PAUSED' | 'DRAFT' | 'ACTIVE' }

export interface XLineItem {
  id: string;
  name?: string;
  campaignId?: string;
  objective?: string;
  productType?: string;
  placements?: string[];
  bidStrategy?: string;
  bidAmountLocalMicro?: number;
  goal?: string;
  payBy?: string;
  entityStatus?: string;
  startTime?: string;
  endTime?: string;
  dailyBudgetLocalMicro?: number;
  totalBudgetLocalMicro?: number;
  currency?: string;
  advertiserDomain?: string;
  audienceExpansion?: string;
  frequencyCap?: number;
  durationInDays?: number;
  createdAt?: string;
  updatedAt?: string;
  /** Only on create, when targeting was sent with it. */
  targeting?: XTargetingResult;
  targetingError?: string;
}

/** The body PUT line-items/{id}/targeting reads. */
export interface XTargetingInput {
  locations?: string[];
  languages?: string[];
  interests?: string[];
  platforms?: string[];
  /** "1" male / "2" female; omit for everyone. */
  gender?: string;
  ageBucket?: string;
  keywords?: Array<{ text: string; matchType: 'BROAD' | 'PHRASE' | 'EXACT' | 'UNORDERED'; exclude?: boolean }>;
  customAudiences?: string[];
  excludedCustomAudiences?: string[];
}
export interface XTargetingResult { removed: number; added: number }

export interface XLineItemInput {
  campaignId: string;
  name: string;
  objective: string;
  productType?: string;
  placements?: string[];
  bidStrategy?: string;
  bidAmountMicros?: number;
  goal?: string;
  advertiserDomain?: string;
  startTime?: string;
  endTime?: string;
  status?: 'PAUSED' | 'DRAFT' | 'ACTIVE';
  targeting?: XTargetingInput;
}

// ── Creatives ─────────────────────────────────────────────────────────────────
export interface XPost { id?: string; text?: string; name?: string; cardUri?: string; mediaUrls?: string[]; createdAt?: string; nullcast?: boolean }
export interface XPromotedPost { id: string; lineItemId?: string; tweetId: string; entityStatus?: string; approvalStatus?: string; post?: XPost }
export interface XMedia { mediaKey?: string; mediaUrl?: string; name?: string; fileName?: string; mediaCategory?: string; createdAt?: string }
export interface XCardInput { name: string; mediaKey: string; title: string; websiteUrl?: string; leadFormId?: string }

// ── Lead forms, leads, audiences ──────────────────────────────────────────────
export const LEAD_FIELD_KEYS = [
  'EMAIL', 'PHONE_NUMBER', 'STREET_ADDRESS', 'CITY', 'STATE', 'COUNTRY', 'ZIP_CODE', 'FULL_NAME', 'FIRST_NAME', 'LAST_NAME',
  'DATE_OF_BIRTH', 'COMPANY_NAME', 'JOB_TITLE', 'WORK_EMAIL', 'WORK_PHONE_NUMBER', 'GENDER', 'HANDLE',
] as const;

export interface XLeadForm {
  id: string;
  name?: string;
  purpose?: string;
  fields?: Array<{ key?: string; optional?: boolean; [k: string]: unknown }>;
  privacyPolicy?: Record<string, unknown>;
  introduction?: Record<string, unknown>;
  finalPage?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  sync?: { lastRunAt?: string; syncedUntil?: string; pending: boolean; lastError?: string; leadCount: number };
}

export interface XLeadFormInput {
  name: string;
  purpose: string;
  fields: Array<{ key: string; optional: boolean }>;
  privacyPolicy: { url: string; companyName: string };
  introduction?: { headline: string; description?: string };
  finalPage?: { headline: string; description?: string; ctaButton?: { url: string; title: string } };
}

export interface XLeadSyncResult { status: 'done' | 'pending' | 'failed' | 'up_to_date' | 'skipped'; added?: number; error?: string }
export interface XLead { leadUid?: string; leadFormId?: string; email?: string; phone?: string; profileName?: string; fields?: Record<string, string>; submittedAt?: string; receivedAt?: string }

export interface XAudience {
  id: string;
  name?: string;
  description?: string;
  audienceType?: string;
  audienceSize?: number;
  targetable?: boolean;
  targetableTypes?: string[];
  reasonsNotTargetable?: string[];
  partnerSource?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ── Reporting ─────────────────────────────────────────────────────────────────
export type XReportLevel = 'campaign' | 'line_item' | 'promoted_tweet';
export interface XMetrics {
  impressions: number; engagements: number; clicks: number; urlClicks: number; follows: number; likes: number; reposts: number; replies: number; videoViews: number;
  /** Currency units (not micros). */
  spend: number;
}
export interface XAggregateRow extends XMetrics { entityId: string; entityName?: string }
export interface XDailyRow extends XMetrics { date: string }
export interface XSyncResult { upserted: number; level: XReportLevel; days: number; entities: number }

type Range = { level?: XReportLevel; from?: string; to?: string };
/** Status changes, deletes and detaches answer `{ ok: true }`. */
type Ok = { ok: boolean };

export const RECONNECT_CODE = 'X_RECONNECT_REQUIRED';
/** X refused the connection's tokens; only a new "Connect with X" fixes it. */
export const isReconnectError = (e: unknown): boolean => (e as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code === RECONNECT_CODE;

const reconnectListeners = new Set<(message: string) => void>();
export function onReconnectRequired(listener: (message: string) => void): () => void {
  reconnectListeners.add(listener);
  return () => { reconnectListeners.delete(listener); };
}
/** The message to show for a caught request error (X's own reason when the backend relayed one). */
export const errMsg = (e: unknown, fallback = 'Something went wrong.'): string => {
  const err = e as { response?: { data?: { message?: string } }; message?: string } | undefined;
  return err?.response?.data?.message || err?.message || fallback;
};
/** The shared client already toasted this error (any reply from the server, or a network failure). */
export const alreadyToasted = (e: unknown): boolean => !!(e as { isAxiosError?: boolean } | undefined)?.isAxiosError;

const BASE = '/slighter_crm/x-ads';
const acc = (uid: string) => `${BASE}/accounts/${encodeURIComponent(uid)}`;
const id = (v: string) => encodeURIComponent(v);
// Every reply passes here so a reconnect refusal reaches the shell on any screen.
async function run<T>(p: Promise<{ data: T }>): Promise<T> {
  try { return (await p).data; }
  catch (e) {
    if (isReconnectError(e)) reconnectListeners.forEach((l) => l(errMsg(e)));
    throw e;
  }
}

export const xAdsService = {
  config: (): Promise<XConfig> => run(api.get<XConfig>(`${BASE}/config`)),
  constants: (): Promise<XConstants> => run(api.get<XConstants>(`${BASE}/constants`)),
  oauthStart: (sandbox: boolean): Promise<{ url: string; sandbox: boolean }> => run(api.get(`${BASE}/oauth/start`, { params: { sandbox } })),

  // Connections
  list: async (): Promise<XAccount[]> => (await run(api.get<XAccount[]>(`${BASE}/accounts`))) ?? [],
  disconnect: (uid: string): Promise<Ok> => run(api.delete<Ok>(acc(uid))),
  health: (uid: string): Promise<XHealth> => run(api.get<XHealth>(`${acc(uid)}/health`, { expectedStatuses: [400, 404, 500, 502, 503] })),
  adsAccounts: async (uid: string): Promise<XAdsAccountOption[]> => (await run(api.get<XAdsAccountOption[]>(`${acc(uid)}/ads-accounts`))) ?? [],
  select: (uid: string, adsAccountId: string, fundingInstrumentId?: string): Promise<XAccount> =>
    run(api.post<XAccount>(`${acc(uid)}/select`, { adsAccountId, fundingInstrumentId }, { timeout: 60_000 })),
  fundingInstruments: async (uid: string): Promise<XFundingInstrument[]> => (await run(api.get<XFundingInstrument[]>(`${acc(uid)}/funding-instruments`))) ?? [],
  createSandboxAccount: (uid: string, currency: string): Promise<XAccount> => run(api.post<XAccount>(`${acc(uid)}/sandbox-account`, { currency }, { timeout: 90_000 })),
  lookup: async (uid: string, kind: XLookupKind, q?: string): Promise<XTargetingOption[]> =>
    (await run(api.get<XTargetingOption[]>(`${acc(uid)}/targeting/${kind}`, { params: { q } }))) ?? [],

  // Campaigns
  campaigns: async (uid: string): Promise<XCampaign[]> => (await run(api.get<XCampaign[]>(`${acc(uid)}/campaigns`))) ?? [],
  createCampaign: (uid: string, body: XCampaignInput): Promise<XCampaign> => run(api.post<XCampaign>(`${acc(uid)}/campaigns`, body)),
  pauseCampaign: (uid: string, campaignId: string): Promise<Ok> => run(api.post<Ok>(`${acc(uid)}/campaigns/${id(campaignId)}/pause`)),
  resumeCampaign: (uid: string, campaignId: string): Promise<Ok> => run(api.post<Ok>(`${acc(uid)}/campaigns/${id(campaignId)}/resume`)),
  deleteCampaign: (uid: string, campaignId: string): Promise<Ok> => run(api.delete<Ok>(`${acc(uid)}/campaigns/${id(campaignId)}`)),

  // Line items + targeting
  lineItems: async (uid: string, campaignId?: string): Promise<XLineItem[]> => (await run(api.get<XLineItem[]>(`${acc(uid)}/line-items`, { params: { campaignId } }))) ?? [],
  createLineItem: (uid: string, body: XLineItemInput): Promise<XLineItem> => run(api.post<XLineItem>(`${acc(uid)}/line-items`, body, { timeout: 60_000 })),
  pauseLineItem: (uid: string, lineItemId: string): Promise<Ok> => run(api.post<Ok>(`${acc(uid)}/line-items/${id(lineItemId)}/pause`)),
  resumeLineItem: (uid: string, lineItemId: string): Promise<Ok> => run(api.post<Ok>(`${acc(uid)}/line-items/${id(lineItemId)}/resume`)),
  deleteLineItem: (uid: string, lineItemId: string): Promise<Ok> => run(api.delete<Ok>(`${acc(uid)}/line-items/${id(lineItemId)}`)),
  setTargeting: (uid: string, lineItemId: string, body: XTargetingInput): Promise<XTargetingResult> =>
    run(api.put<XTargetingResult>(`${acc(uid)}/line-items/${id(lineItemId)}/targeting`, body, { timeout: 60_000 })),

  // Promoted posts
  promoted: async (uid: string, lineItemId: string): Promise<XPromotedPost[]> => (await run(api.get<XPromotedPost[]>(`${acc(uid)}/line-items/${id(lineItemId)}/promoted-posts`))) ?? [],
  promote: async (uid: string, lineItemId: string, tweetIds: string[]): Promise<XPromotedPost[]> =>
    (await run(api.post<XPromotedPost[]>(`${acc(uid)}/line-items/${id(lineItemId)}/promoted-posts`, { tweetIds }))) ?? [],
  unpromote: (uid: string, promotedId: string): Promise<Ok> => run(api.delete<Ok>(`${acc(uid)}/promoted-posts/${id(promotedId)}`)),

  // Posts, images, cards
  posts: async (uid: string, type = 'PUBLISHED', timeline = 'NULLCAST'): Promise<XPost[]> =>
    (await run(api.get<XPost[]>(`${acc(uid)}/posts`, { params: { type, timeline }, timeout: 60_000 }))) ?? [],
  createPost: (uid: string, body: { text?: string; mediaKeys?: string[]; cardUri?: string; name?: string }): Promise<XPost> => run(api.post<XPost>(`${acc(uid)}/posts`, body)),
  /** Multipart field `file`. */
  uploadImage: (uid: string, file: File): Promise<{ mediaKey: string; mediaUrl?: string; name?: string }> => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', file.name);
    return run(api.post(`${acc(uid)}/media/images`, form, { timeout: 120_000, headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  media: async (uid: string): Promise<XMedia[]> => (await run(api.get<XMedia[]>(`${acc(uid)}/media`, { timeout: 60_000 }))) ?? [],
  createCard: (uid: string, body: XCardInput): Promise<{ id?: string; cardUri?: string; name?: string }> => run(api.post(`${acc(uid)}/cards`, body)),

  // Lead forms + leads
  leadForms: async (uid: string): Promise<XLeadForm[]> => (await run(api.get<XLeadForm[]>(`${acc(uid)}/lead-forms`))) ?? [],
  createLeadForm: (uid: string, body: XLeadFormInput): Promise<XLeadForm> => run(api.post<XLeadForm>(`${acc(uid)}/lead-forms`, body)),
  deleteLeadForm: (uid: string, formId: string): Promise<Ok> => run(api.delete<Ok>(`${acc(uid)}/lead-forms/${id(formId)}`)),
  syncLeadForm: (uid: string, formId: string): Promise<XLeadSyncResult> => run(api.post<XLeadSyncResult>(`${acc(uid)}/lead-forms/${id(formId)}/sync`, {}, { timeout: 90_000 })),
  leads: async (uid: string, formId?: string, limit = 500): Promise<XLead[]> => (await run(api.get<XLead[]>(`${acc(uid)}/leads`, { params: { formId, limit } }))) ?? [],

  // Audiences
  audiences: async (uid: string): Promise<XAudience[]> => (await run(api.get<XAudience[]>(`${acc(uid)}/audiences`))) ?? [],
  audienceFromGroup: (uid: string, body: { groupUid: string; name: string; description?: string }): Promise<{ audienceId: string; contactsInGroup: number; usersUploaded: number }> =>
    run(api.post(`${acc(uid)}/audiences/from-group`, body, { timeout: 180_000 })),

  // Reporting
  syncReport: (uid: string, daysBack: number, level: XReportLevel): Promise<XSyncResult> => run(api.post<XSyncResult>(`${acc(uid)}/reporting/sync`, { daysBack, level }, { timeout: 300_000 })),
  /** `silent`: the caller treats a failure as "no results yet" (the campaign list merges them when present). */
  aggregate: async (uid: string, r: Range, silent = false): Promise<XAggregateRow[]> =>
    (await run(api.get<XAggregateRow[]>(`${acc(uid)}/reporting/aggregate`, { params: r, ...(silent ? { expectedStatuses: [400, 404, 500, 502, 503] } : {}) }))) ?? [],
  trend: async (uid: string, r: Range): Promise<XDailyRow[]> => (await run(api.get<XDailyRow[]>(`${acc(uid)}/reporting/trend`, { params: r }))) ?? [],
  bulkDaily: async (uid: string, entityIds: string[], r: Range): Promise<Record<string, XDailyRow[]>> =>
    (await run(api.get<Record<string, XDailyRow[]>>(`${acc(uid)}/reporting/bulk-daily`, { params: { ...r, entityIds: entityIds.join(',') } }))) ?? {},
};
